import { LEVELS } from '../content/words';
import type { Progress } from '../game/progress';
import type { AskKind } from '../game/questions';
import type { StyleId } from '../theme/themes';
import { $ } from './dom';

export interface SettingsDeps {
  progress: Progress;
  /** A study setting changed (level, what is asked, new words a day). */
  onStudy(): void;
  onStyle(style: StyleId): void;
}

/**
 * The settings in the You tab: level, what is asked, new words a day and style. The sound,
 * wobble and detail controls next to them are wired by the game.
 */
export class Settings {
  private readonly el = $('tab-you');
  private readonly levelBtns = [...this.el.querySelectorAll<HTMLButtonElement>('[data-level]')];
  private readonly askBtns = [...this.el.querySelectorAll<HTMLButtonElement>('[data-ask]')];
  private readonly newBtns = [...this.el.querySelectorAll<HTMLButtonElement>('[data-new]')];
  private readonly styleBtns = [...this.el.querySelectorAll<HTMLButtonElement>('[data-style]')];
  private readonly lookBtns = [...this.el.querySelectorAll<HTMLButtonElement>('[data-look]')];

  constructor(private readonly d: SettingsDeps) {
    const { progress } = d;
    this.levelBtns.forEach(b => b.addEventListener('click', () => { progress.setMaxLevel(Number(b.dataset.level)); this.render(); d.onStudy(); }));
    // toggles; the last one left on can't be turned off, since a bubble must ask something
    this.askBtns.forEach(b => b.addEventListener('click', () => {
      const kind = b.dataset.ask as AskKind, ask = progress.data.settings.ask;
      progress.setAsk(ask.includes(kind) ? ask.filter(k => k !== kind) : [...ask, kind]);
      this.render(); d.onStudy();
    }));
    this.newBtns.forEach(b => b.addEventListener('click', () => { progress.setNewPerDay(Number(b.dataset.new)); this.render(); d.onStudy(); }));
    this.styleBtns.forEach(b => b.addEventListener('click', () => {
      const style = b.dataset.style as StyleId;
      if (style === progress.data.settings.style) return;
      progress.setStyle(style);
      this.render();
      d.onStyle(style);
    }));
    // light, dark, or auto (following the device)
    this.lookBtns.forEach(b => b.addEventListener('click', () => {
      const look = b.dataset.look === 'auto' ? null : b.dataset.look as 'light' | 'dark';
      if (look === progress.data.settings.appearance) return;
      progress.setAppearance(look);
      this.render();
      d.onStyle(progress.data.settings.style);
    }));
    // the sound buttons are wired by the game; their group's header follows them
    for (const id of ['sound', 'slowToggle']) document.getElementById(id)?.addEventListener('click', () => setTimeout(() => this.render(), 0));
    this.render();
  }

  render(): void {
    const s = this.d.progress.data.settings;
    this.levelBtns.forEach(b => { const lv = Number(b.dataset.level); b.hidden = !LEVELS.includes(lv); b.setAttribute('aria-checked', String(lv === s.maxLevel)); });
    this.askBtns.forEach(b => { const on = s.ask.includes(b.dataset.ask as AskKind); b.setAttribute('aria-pressed', String(on)); b.disabled = on && s.ask.length === 1; });
    this.newBtns.forEach(b => b.setAttribute('aria-checked', String(Number(b.dataset.new) === s.newPerDay)));
    this.styleBtns.forEach(b => b.setAttribute('aria-checked', String(b.dataset.style === s.style)));
    this.lookBtns.forEach(b => b.setAttribute('aria-checked', String(b.dataset.look === (s.appearance ?? 'auto'))));
    // what each folded group is set to, in its header
    const put = (id: string, text: string) => { const e = document.getElementById(id); if (e) e.textContent = text; };
    put('gStudyV', `HSK ${s.maxLevel === 1 ? '1' : `1–${s.maxLevel}`} · ${s.newPerDay} new a day`);
    put('gLookV', `${s.style === 'ink' ? 'Ink & lanterns' : 'Bubbles'} · ${s.appearance === 'light' ? 'Light' : s.appearance === 'dark' ? 'Dark' : 'Auto'}`);
    const on = document.getElementById('sound')?.getAttribute('aria-pressed') !== 'false';
    const slow = document.getElementById('slowToggle')?.getAttribute('aria-pressed') === 'true';
    put('gSoundV', on ? (slow ? 'On · slow voice' : 'On') : 'Off');
  }
}
